<template>
  <div
    v-if="isFree || price?.visible || isSubscribedPrice"
    class="plan"
    :class="{
      compact: mode === 'compact',
      vertical: layout === 'vertical',
      current: isCurrent,
      preselected: preselected,
      selected: selected,
      legacy: isLegacy,
    }"
  >
    <div class="top">
      <div class="identity">
        <div class="title-row">
          <span
            v-if="product.icon"
            class="plan-icon hgi-stroke"
            :class="product.icon"
          ></span>
          <h3>{{ product.name }}</h3>
          <span v-if="isLegacy && !isFree" class="badge">Legacy</span>
          <span v-if="isCurrent" class="badge current">Current</span>
          <span v-else-if="preselected" class="badge suggested">Suggested</span>
        </div>
        <p v-if="product.description" class="description">
          {{ product.description }}
        </p>
      </div>
      <div class="price-section">
        <div class="price">
          <span v-if="priceCurrency" class="currency">{{ priceCurrency }}</span>
          <strong>{{ priceAmount }}</strong>
          <span v-if="priceSuffix" class="suffix">{{ priceSuffix }}</span>
        </div>
        <p v-if="priceInterval" class="interval">{{ priceInterval }}</p>
      </div>
    </div>
    <ul v-if="product.features.length > 0" class="features">
      <li v-for="feature in product.features" :key="feature">
        <span class="tick">
          <TickIcon />
        </span>
        {{ feature }}
      </li>
    </ul>
    <div v-if="!isFree || action.button" class="footer">
      <BaseLink
        v-if="!isFree"
        class="more"
        @click="openUrl('https://routine.co/pricing')"
      >
        <template #icon> <ExternalIcon /> </template>Learn more
      </BaseLink>
      <div v-if="action.button && showAction" class="actions">
        <!-- Custom (non-personal) organizations can book a demo before
             upgrading; the personal plans and the Enterprise card (whose
             main action already is "Book demo") don't offer it. -->
        <BaseButton
          v-if="showBookDemo"
          kind="main"
          type="secondary"
          text="Book demo"
          @click="openUrl('https://routine.co/contact')"
        />
        <BaseButton
          kind="main"
          :type="action.text === 'Downgrade' ? 'secondary' : 'primary'"
          :text="action.text"
          :state="!canManage ? 'disabled' : switching ? 'loading' : 'default'"
          :tooltip="
            canManage
              ? undefined
              : 'Only organization administrators can manage subscriptions.'
          "
          :tooltipDisplay="canManage ? 'none' : 'always'"
          @click="action.callback"
        />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, PropType, ref, toRefs } from "vue";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseLink from "@/components/base/BaseLink.vue";
import ExternalIcon from "@/components/icons/ExternalIcon.vue";
import TickIcon from "@/components/icons/TickIcon.vue";
import * as inject from "@/components/inject";
import { paidPlanChangeText } from "@/components/settings/subscription";
import type {
  BillingPrice,
  BillingProduct,
  BillingSubscription,
} from "@/utils/billing";
import { openExternalUrl } from "@/utils/open-external-url";

export default defineComponent({
  components: {
    BaseButton,
    BaseLink,
    ExternalIcon,
    TickIcon,
  },
  props: {
    layout: {
      type: String as PropType<"default" | "vertical">,
      required: false,
      default: "default",
    },
    mode: {
      type: String as PropType<"expanded" | "compact">,
      required: false,
      default: "expanded",
    },
    product: {
      type: Object as PropType<BillingProduct>,
      required: true,
    },
    // Because the user might be on a Free plan, the Price might be null
    price: {
      type: Object as PropType<BillingPrice>,
      required: false,
    },
    subscription: {
      type: Object as PropType<BillingSubscription | null>,
      required: false,
    },
    preselected: {
      type: Boolean,
      required: false,
    },
    /** Highlight the card as the user's current selection. */
    selected: {
      type: Boolean,
      required: false,
    },
    /**
     * Render the action (Upgrade/Downgrade/…) button. Hosts that provide
     * their own action button (e.g. the onboarding plan step) disable it.
     */
    showAction: {
      type: Boolean,
      required: false,
      default: true,
    },
    canManage: {
      type: Boolean,
      required: false,
      default: true,
    },
    // The organization this plan is displayed for.
    orgId: {
      type: String,
      required: true,
    },
  },
  async setup(props) {
    const { product, subscription } = toRefs(props);

    const controller = inject.controller();
    const billing = inject.billing();
    const organizationId = computed(() => props.orgId);
    const isTrialing = computed(
      () => subscription.value?.status === "trialing"
    );
    // During a trial, no plan is considered subscribed: the trial is called
    // out in the subscription status sentence instead of a plan being shown
    // as selected, so the user can still subscribe to any plan (including
    // the one being trialed).
    const subscribedProduct = computed(() =>
      isTrialing.value ? undefined : subscription.value?.product
    );
    const subscribedPrice = computed(() =>
      isTrialing.value ? undefined : subscription.value?.price
    );
    const isFree = computed(() => product.value.plan === "free");

    const isCurrent = computed(() => {
      return (
        props.product.id === subscribedProduct.value?.id &&
        (isFree.value ||
          (props.price && props.price?.id === subscribedPrice.value?.id))
      );
    });

    // Whether this card matches the actual subscription price, regardless of
    // trial status. Only used to keep hidden (legacy) prices visible.
    const isSubscribedPrice = computed(
      () => !!props.price && props.price.id === subscription.value?.price?.id
    );

    const isLegacy = computed(() => {
      return (!props.price?.visible && !props.price?.available) || false;
    });

    const priceAmount = computed(() => {
      if (isFree.value) {
        return "Free";
      } else {
        if (props.price?.model === "custom") {
          return "Contact us";
        } else {
          return props.price?.interval === "year"
            ? Math.round((props.price?.amount / 12) * 10) / 10
            : props.price?.amount;
        }
      }
    });
    const priceCurrency = computed(() => {
      return isFree.value || props.price?.model === "custom"
        ? ""
        : props.price?.currency;
    });
    const isVertical = computed(() => props.layout === "vertical");
    // Vertical (pricing-page) cards write the price as "$X/month" with the
    // billing cadence underneath, instead of one interval sentence.
    const priceSuffix = computed(() => {
      if (!isVertical.value || isFree.value || props.price?.model === "custom")
        return "";
      return props.price?.model === "seat" ? "/seat/month" : "/month";
    });
    const priceInterval = computed(() => {
      if (isFree.value) {
        return "Forever";
      } else {
        if (props.price?.model === "custom") {
          return "";
        } else if (isVertical.value) {
          return props.price?.interval === "year"
            ? "billed annually"
            : "billed monthly";
        } else {
          const isSeat = props.price?.model === "seat";
          const perMonth = isSeat ? "per seat/month" : "per month";
          return props.price?.interval === "year"
            ? `${perMonth}, billed annually`
            : perMonth;
        }
      }
    });
    const action = computed(() => {
      if (isFree.value) {
        // This product is "Free"
        if (subscribedProduct?.value?.id === props.product.id) {
          // And the user is on it
          return { button: false, text: "", callback: () => {} };
        } else {
          // But the user is on another "premium" plan

          // If the current subscription is been canceled, do not offer to downgrade.
          if (subscription.value?.status === "canceled") {
            return { button: false, text: "", callback: () => {} };
          } else {
            return {
              button: true,
              text: "Downgrade",
              callback: () => {
                switchPrice(props.price || null);
              },
            };
          }
        }
      } else {
        // This product is premium

        // If the user is on the same product/price combination
        if (
          subscribedProduct.value?.id === props.product.id &&
          subscribedPrice.value?.id === props.price?.id
        ) {
          // If the current subscription has been canceled, offer to re-subscribe.
          if (subscription.value?.status === "canceled") {
            return {
              button: true,
              text: "Resubscribe",
              callback: () => {
                switchPrice(props.price || null);
              },
            };
          } else {
            return { button: false, text: "", callback: () => {} };
          }
        }

        // If the product is not available, offer to book a call
        if (!props.price?.available) {
          return {
            button: true,
            text: "Book demo",
            callback: () => {
              openUrl("https://routine.co/contact");
            },
          };
        } else {
          // The product is available but...
          if (!subscribedPrice.value) {
            // But the user does not have any subscription i.e is on the "Free" product
            return {
              button: true,
              text: "Upgrade",
              callback: () => {
                switchPrice(props.price || null);
              },
            };
          } else if (!props.price) {
            return { button: false, text: "", callback: () => {} };
          } else {
            return {
              button: true,
              text: paidPlanChangeText(subscribedPrice.value, props.price, {
                sameProduct: subscribedProduct.value?.id === props.product.id,
                canceled: subscription.value?.status === "canceled",
              }),
              callback: () => {
                switchPrice(props.price || null);
              },
            };
          }
        }
      }
    });

    const openUrl = (url: string) => {
      openExternalUrl(url, controller);
    };

    const isPersonalOrganization = computed(() => {
      const personalOrgId =
        billing.value?.personalOrganizationId?.value ?? null;
      return !!organizationId.value && organizationId.value === personalOrgId;
    });
    // Next to "Upgrade", custom organizations also get a secondary
    // "Book demo" button (same contact page as the Enterprise card's action).
    const showBookDemo = computed(
      () => !isPersonalOrganization.value && action.value.text === "Upgrade"
    );

    const switching = ref(false);

    const switchPrice = async (price: BillingPrice | null) => {
      if (!organizationId.value) {
        window.console.error("Organization ID is not available");
        return;
      }
      if (switching.value) return;
      switching.value = true;
      try {
        await billing.value?.switchSubscription(
          organizationId.value,
          price?.id ?? null
        );
      } finally {
        switching.value = false;
      }
    };

    return {
      openUrl,
      switchPrice,
      switching,
      subscribedProduct,
      subscribedPrice,
      priceAmount,
      priceCurrency,
      priceInterval,
      priceSuffix,
      isCurrent,
      isFree,
      isLegacy,
      isSubscribedPrice,
      showBookDemo,
      action,
    };
  },
});
</script>
<style lang="scss" scoped>
@use "@/styles/_variables.scss" as *;
@use "@/styles/_themes.scss" as *;

.plan {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  border-radius: $border-radius-m;
  @include transition(
    border-color 100ms ease-out,
    background-color 100ms ease-out
  );
  @include themed() {
    border: 1px solid t($grey-15);
    background-color: t($bg);
  }

  &.compact {
    gap: 8px;
    padding: 10px 12px;
  }

  &.current {
    @include themed() {
      border-color: t($grey-80);
      background-color: t($grey-03);
    }
  }

  &.preselected:not(.current) {
    @include themed() {
      border-color: t($tertiary-50);
    }
  }

  &.selected {
    @include themed() {
      border-color: t($tertiary-100);
      background-color: t($tertiary-10);
    }
  }

  &.legacy:not(.current) {
    @include themed() {
      border-color: t($grey-20);
    }
  }

  // Pricing-page-like vertical card (routine.co/pricing): icon above the
  // name, price under the description, action button above a single-column
  // feature list. Used by the onboarding plan step.
  &.vertical {
    text-align: start;

    .top {
      flex-direction: column;
      align-items: stretch;
      gap: 16px;
    }

    .title-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }

    .plan-icon {
      width: 20px;
      height: 20px;
      font-size: 20px;
    }

    h3 {
      @extend %font-h4;
    }

    .description {
      margin-top: 4px;
    }

    .price-section {
      align-items: flex-start;
      text-align: start;
      gap: 2px;
    }

    .price strong {
      @extend %font-h3;
    }

    // The action comes right after the price, above the features, like the
    // pricing page ("Get started" above "Includes:").
    .footer {
      order: 1;
      margin-top: 4px;
    }

    .features {
      order: 2;
      grid-template-columns: 1fr;
      padding-top: 12px;
    }
  }
}

.top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.identity {
  min-width: 0;
  flex: 1;
}

.title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.plan-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  font-size: 14px;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  @include transition(color 100ms ease-out);
  @include themed() {
    color: t($grey-80);
  }
}

h3 {
  @extend %font-body-medium;
  @extend %font-smooth;
  margin: 0;
}

.badge {
  @extend %font-small-medium;
  padding: 1px 6px;
  border-radius: $border-radius-s;
  @include themed() {
    background-color: t($grey-05);
    color: t($grey-60);
  }

  &.current {
    @include themed() {
      background-color: t($grey-10);
      color: t($grey-80);
    }
  }

  &.suggested {
    @include themed() {
      background-color: t($tertiary-10);
      color: t($tertiary-100);
    }
  }
}

.description {
  @extend %font-small-regular;
  margin: 2px 0 0;
  @include themed() {
    color: t($grey-60);
  }
}

.price-section {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  text-align: end;
}

.price {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.currency,
.suffix {
  @extend %font-caption-regular;
  @include themed() {
    color: t($grey-60);
  }
}

strong {
  @extend %font-h4;
  @extend %font-smooth;
  margin: 0;
}

.interval {
  @extend %font-small-regular;
  margin: 0;
  @include themed() {
    color: t($grey-60);
  }
}

.features {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 16px;
  margin: 0;
  padding: 8px 0 0;
  list-style: none;
  @include themed() {
    border-top: 1px solid t($grey-15);
  }

  li {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-70);
    }
  }

  .compact & {
    padding-top: 6px;
    gap: 4px 12px;
  }

  @include media("<=medium") {
    grid-template-columns: 1fr;
  }
}

.tick {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 14px;
  height: 14px;

  :deep(svg) {
    display: block;
    width: 12px;
    height: 12px;
  }

  :deep(.stroke) {
    @include themed() {
      stroke: t($grey-60);
    }
  }
}

.footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: auto;
}

.more {
  margin: 0;
}

.actions {
  display: flex;
  align-items: center;
  gap: 8px;
  // Keeps the buttons at the end even when the "Learn more" link is absent
  // (the free card's Downgrade) — the footer's space-between can't do it.
  margin-inline-start: auto;
}

.base-button {
  width: auto;
  justify-content: center;
  margin: 0;

  &.disabled {
    @include themed() {
      background-color: t($grey-10);
    }
  }
}
</style>
